/**
 * DonationCMSTab — CMS-controlled donation-page content per campaign.
 * Reads/writes ONLY the `page_config.donation_cms` sub-key on the campaign
 * via /api/campaigns/admin/{id}/donation-cms. The Campaign module's layout
 * remains untouched — this controls presentation/content only.
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { MediaUpload } from '@/components/MediaUpload';

const RECURRING_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const DEFAULTS = {
  banner_video_url: '',
  thank_you_message: '',
  thank_you_video_url: '',
  donor_instructions: '',
  default_donor_visibility: 'public',
  suggested_amounts: [],
  giving_categories: [],
  enable_recurring: false,
  recurring_intervals: ['monthly'],
  donor_form_fields: {
    show_phone: true,
    phone_required: false,
    show_message: true,
    show_address: false,
  },
  payment_instructions_html: '',
  receipt_footer_html: '',
};

const DonationCMSTab = ({ campaignId }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-donation-cms', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/donation-cms`).then((r) => r.data),
    enabled: !!campaignId,
  });

  useEffect(() => {
    if (data && !form) {
      setForm({ ...DEFAULTS, ...data, donor_form_fields: { ...DEFAULTS.donor_form_fields, ...(data.donor_form_fields || {}) } });
    }
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.put(`/campaigns/admin/${campaignId}/donation-cms`, payload),
    onSuccess: () => {
      toast.success('Donation page settings saved');
      queryClient.invalidateQueries({ queryKey: ['campaign-donation-cms', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || 'Save failed'),
  });

  if (isLoading || !form) {
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading donation CMS…
      </div>
    );
  }

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateFields = (patch) => setForm((f) => ({ ...f, donor_form_fields: { ...f.donor_form_fields, ...patch } }));

  // ---------- suggested amounts ----------
  const addSuggested = () => update({ suggested_amounts: [...(form.suggested_amounts || []), 50] });
  const updateSuggested = (idx, val) => {
    const arr = [...form.suggested_amounts];
    arr[idx] = Number(val) || 0;
    update({ suggested_amounts: arr });
  };
  const removeSuggested = (idx) => update({ suggested_amounts: form.suggested_amounts.filter((_, i) => i !== idx) });

  // ---------- giving categories ----------
  const addCategory = () => update({ giving_categories: [...(form.giving_categories || []), { id: `cat-${Date.now()}`, name: '', description: '' }] });
  const updateCategory = (idx, patch) => {
    const arr = [...form.giving_categories];
    arr[idx] = { ...arr[idx], ...patch };
    update({ giving_categories: arr });
  };
  const removeCategory = (idx) => update({ giving_categories: form.giving_categories.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4" data-testid="donation-cms-tab">
      {/* ---------- BANNER + INSTRUCTIONS ---------- */}
      <Card>
        <CardHeader><CardTitle className="text-base">Banner & Donor Instructions</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Banner video link <span className="text-xs text-slate-400">(YouTube / Vimeo — optional)</span></Label>
            <Input
              value={/^https?:\/\//i.test(form.banner_video_url || '') ? form.banner_video_url : ''}
              onChange={(e) => update({ banner_video_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
              data-testid="cms-banner-video"
            />
            <MediaUpload
              value={/^https?:\/\//i.test(form.banner_video_url || '') ? '' : form.banner_video_url}
              onChange={(url) => update({ banner_video_url: url })}
              purpose="video" label="…or upload a video file"
              testId="cms-banner-video-upload"
              className="mt-2"
            />
          </div>
          <div>
            <Label>Donor instructions <span className="text-xs text-slate-400">(shown above the donation form, HTML allowed)</span></Label>
            <Textarea
              rows={3}
              value={form.donor_instructions}
              onChange={(e) => update({ donor_instructions: e.target.value })}
              placeholder="A short note to donors — purpose, urgency, payment guidance…"
              data-testid="cms-donor-instructions"
            />
          </div>
          <div>
            <Label>Global payment instructions <span className="text-xs text-slate-400">(shown in the payment-method panel)</span></Label>
            <Textarea
              rows={3}
              value={form.payment_instructions_html}
              onChange={(e) => update({ payment_instructions_html: e.target.value })}
              placeholder="Bank details, EcoCash codes, or general guidance — HTML allowed"
              data-testid="cms-payment-instructions"
            />
          </div>
        </CardContent>
      </Card>

      {/* ---------- SUGGESTED AMOUNTS ---------- */}
      <Card>
        <CardHeader><CardTitle className="text-base">Suggested Amounts</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">Leave empty to use the default set (25, 50, 100, 250, 500, 1000). Max 12 entries.</p>
          <div className="flex flex-wrap gap-2">
            {(form.suggested_amounts || []).map((amt, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <Input
                  type="number" min="1"
                  className="w-24"
                  value={amt}
                  onChange={(e) => updateSuggested(idx, e.target.value)}
                  data-testid={`cms-suggested-amount-${idx}`}
                />
                <Button size="icon" variant="ghost" onClick={() => removeSuggested(idx)} data-testid={`cms-remove-amount-${idx}`}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
            {(form.suggested_amounts || []).length < 12 && (
              <Button size="sm" variant="outline" onClick={addSuggested} data-testid="cms-add-amount">
                <Plus className="h-3 w-3 mr-1" /> Add amount
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ---------- GIVING CATEGORIES ---------- */}
      <Card>
        <CardHeader><CardTitle className="text-base">Giving Categories</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">Optional — let donors tag what their gift is for (e.g. &quot;Building Fund&quot;, &quot;Missions&quot;).</p>
          {(form.giving_categories || []).map((cat, idx) => (
            <div key={cat.id || idx} className="flex gap-2 items-start border rounded p-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input
                  placeholder="Category name"
                  value={cat.name}
                  onChange={(e) => updateCategory(idx, { name: e.target.value })}
                  data-testid={`cms-category-name-${idx}`}
                />
                <Input
                  placeholder="Short description (optional)"
                  value={cat.description || ''}
                  onChange={(e) => updateCategory(idx, { description: e.target.value })}
                  data-testid={`cms-category-desc-${idx}`}
                />
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeCategory(idx)} data-testid={`cms-remove-category-${idx}`}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addCategory} data-testid="cms-add-category">
            <Plus className="h-3 w-3 mr-1" /> Add category
          </Button>
        </CardContent>
      </Card>

      {/* ---------- RECURRING / PRIVACY / FORM FIELDS ---------- */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recurring, Privacy & Form Fields</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Enable recurring giving</Label>
            <Switch
              checked={!!form.enable_recurring}
              onCheckedChange={(v) => update({ enable_recurring: v })}
              data-testid="cms-enable-recurring"
            />
          </div>
          {form.enable_recurring && (
            <div className="pl-4 space-y-2">
              <Label className="text-xs text-slate-500">Allowed intervals</Label>
              <div className="flex gap-3">
                {RECURRING_OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={(form.recurring_intervals || []).includes(o.value)}
                      onChange={(e) => {
                        const set = new Set(form.recurring_intervals || []);
                        if (e.target.checked) set.add(o.value); else set.delete(o.value);
                        update({ recurring_intervals: Array.from(set) });
                      }}
                      data-testid={`cms-recurring-${o.value}`}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Default donor visibility</Label>
            <Select
              value={form.default_donor_visibility}
              onValueChange={(v) => update({ default_donor_visibility: v })}
            >
              <SelectTrigger data-testid="cms-default-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (name shown)</SelectItem>
                <SelectItem value="anonymous">Anonymous</SelectItem>
                <SelectItem value="private">Private (admin only)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <label className="flex items-center justify-between text-sm">
              Show phone field
              <Switch
                checked={form.donor_form_fields.show_phone}
                onCheckedChange={(v) => updateFields({ show_phone: v })}
                data-testid="cms-field-phone"
              />
            </label>
            <label className="flex items-center justify-between text-sm">
              Require phone
              <Switch
                checked={form.donor_form_fields.phone_required}
                onCheckedChange={(v) => updateFields({ phone_required: v })}
                disabled={!form.donor_form_fields.show_phone}
                data-testid="cms-field-phone-required"
              />
            </label>
            <label className="flex items-center justify-between text-sm">
              Show message field
              <Switch
                checked={form.donor_form_fields.show_message}
                onCheckedChange={(v) => updateFields({ show_message: v })}
                data-testid="cms-field-message"
              />
            </label>
            <label className="flex items-center justify-between text-sm">
              Show address field
              <Switch
                checked={form.donor_form_fields.show_address}
                onCheckedChange={(v) => updateFields({ show_address: v })}
                data-testid="cms-field-address"
              />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* ---------- THANK YOU + RECEIPT ---------- */}
      <Card>
        <CardHeader><CardTitle className="text-base">Thank-you & Receipt</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Thank-you message <span className="text-xs text-slate-400">(shown after a successful donation, HTML allowed)</span></Label>
            <Textarea
              rows={3}
              value={form.thank_you_message}
              onChange={(e) => update({ thank_you_message: e.target.value })}
              data-testid="cms-thank-you-message"
            />
          </div>
          <div>
            <Label>Thank-you video link <span className="text-xs text-slate-400">(YouTube / Vimeo — optional)</span></Label>
            <Input
              value={/^https?:\/\//i.test(form.thank_you_video_url || '') ? form.thank_you_video_url : ''}
              onChange={(e) => update({ thank_you_video_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
              data-testid="cms-thank-you-video"
            />
            <MediaUpload
              value={/^https?:\/\//i.test(form.thank_you_video_url || '') ? '' : form.thank_you_video_url}
              onChange={(url) => update({ thank_you_video_url: url })}
              purpose="video" label="…or upload a video file"
              testId="cms-thank-you-video-upload"
              className="mt-2"
            />
          </div>
          <div>
            <Label>Receipt footer <span className="text-xs text-slate-400">(appended to email/SMS receipts, HTML allowed)</span></Label>
            <Textarea
              rows={2}
              value={form.receipt_footer_html}
              onChange={(e) => update({ receipt_footer_html: e.target.value })}
              data-testid="cms-receipt-footer"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end sticky bottom-0 bg-white pt-3 pb-1">
        <Button
          onClick={() => saveMutation.mutate(form)}
          disabled={saveMutation.isPending}
          data-testid="cms-save-btn"
        >
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Donation Page
        </Button>
      </div>
    </div>
  );
};

export default DonationCMSTab;
